import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Form, Input, message } from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import "./Login.scss";
import { BASE_API_URL } from "../../utils/consts";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setUserData } from "../../utils/redux/slices/userDataSlice";
import { resetPermissions } from "../../utils/redux/slices/permissionsSlice";
import { ReactComponent as LogoIcon } from "../../assets/fixmanLogo.svg";
import { setLoader } from "../../utils/redux/slices/loaderSlice";

type FieldType = {
  email?: string;
  password?: string;
  remember?: string;
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form: any = useRef(null);
  const [error, setError] = useState<any>(undefined);
  const [activeAccount, setActivateAccount] = useState<any>(undefined);
  const [forgotPassword, setForgotPassword] = useState<any>(false);

  const onFinish = (values: any) => {
    dispatch(setLoader(true));
    axios
      .post(`${BASE_API_URL}/auth/signin`, {
        email: values.email,
        password: values.password,
      })
      .then(function (response) {
        dispatch(resetPermissions());
        dispatch(setUserData(response.data));
        localStorage.setItem("user", JSON.stringify(response.data));
        navigate("/");
      })
      .catch(function (error) {
        setError(error.response.data.message);
      })
      .finally(function () {
        dispatch(setLoader(false));
      });
  };

  useEffect(() => {
    form?.current?.getFieldInstance("email").focus();
  }, []);

  const onFinishFailed = (errorInfo: any) => {
    console.error("Failed:", errorInfo);
  };

  const onFinishForgotPassword = async (values: any) => {
    try {
      dispatch(setLoader(true));
      await axios.post(`${BASE_API_URL}/forgot-password`, { email: values.email });
      message.success(
        t("Reset instructions have been sent to your email if the account exists."),
      );
      setForgotPassword(false);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || t("Something went wrong, try again."),
      );
    } finally {
      dispatch(setLoader(false));
    }
  };

  const reactivateAccount = (email: any) => {
    axios
      .post(`${BASE_API_URL}/activateAccount`, { email })
      .then(function (response) {
        setActivateAccount(response.data);
      })
      .catch(function (error) {
        setError(error.response.data.message);
      })
      .finally(function () {
        dispatch(setLoader(false));
      });
  };

  return (
    <div className="loginPage">
      <div className="loginBrandPanel hide-md">
        <div className="brandContent">
          <div className="brandLogo">
            <LogoIcon className="brandLogoSvg" />
          </div>
          <div className="brandTagline">People Operations, simplified</div>
          <h2 className="brandHero">
            Tvoj tim,<br />na jednom<br />mjestu.
          </h2>
          <p className="brandDescription">
            Upravljaj platnim listama, odsustvima i timskim performansama — sve na jednoj platformi.
          </p>
          <div className="brandStats">
            <div className="statItem">
              <span className="statNumber">40+</span>
              <span className="statLabel">kompanija</span>
            </div>
            <div className="statDivider" />
            <div className="statItem">
              <span className="statNumber">1.500+</span>
              <span className="statLabel">korisnika</span>
            </div>
            <div className="statDivider" />
            <div className="statItem">
              <span className="statNumber">98%</span>
              <span className="statLabel">zadovoljstvo</span>
            </div>
          </div>
        </div>
      </div>

      <div className="loginFormPanel">
        <div className="formPanelInner">
          <div className="loginLogoMobile show-md">
            <LogoIcon className="mobileLogoSvg" />
          </div>

          {!forgotPassword ? (
            <>
              <div className="loginFormHeader">
                <h1 className="loginTitle">Dobrodošli nazad</h1>
                <p className="loginSubtitle">Prijavi se na svoj Fixman HR nalog.</p>
              </div>

              <Form
                name="basic"
                ref={form}
                layout="vertical"
                style={{ width: "100%" }}
                initialValues={{ remember: true }}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
              >
                <Form.Item<FieldType>
                  name="email"
                  label={t("Email")}
                  rules={[{ required: true, message: t("Please input your email!") }]}
                >
                  <Input placeholder="vas@email.ba" size="large" />
                </Form.Item>

                <Form.Item<FieldType>
                  name="password"
                  label={t("Password")}
                  rules={[{ required: true, message: t("Please input your password!") }]}
                >
                  <Input.Password placeholder="••••••••" size="large" />
                </Form.Item>

                <div className="formRow">
                  <Form.Item<FieldType> name="remember" valuePropName="checked" noStyle>
                    <Checkbox>Zapamti me na ovom uređaju</Checkbox>
                  </Form.Item>
                  <span
                    className="forgotPasswordLink"
                    onClick={() => setForgotPassword(true)}
                  >
                    {t("Forgot password")}
                  </span>
                </div>

                {error && (
                  <div className="errorLogin">
                    {!activeAccount?.message ? <p>{t(`${error}`)}</p> : null}
                    <p>{activeAccount ? t(`${activeAccount?.message}`) : ""}</p>
                    {error.includes("User account is not activated.") && (
                      <div
                        className="emailActivationLink"
                        onClick={() => {
                          const email = form.current?.getFieldValue("email");
                          if (email) {
                            reactivateAccount(email);
                          } else {
                            setError(
                              "Please enter your email before requesting reactivation.",
                            );
                          }
                        }}
                      >
                        Activate
                      </div>
                    )}
                  </div>
                )}

                <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    className="submitBtn"
                  >
                    Prijavi se
                  </Button>
                </Form.Item>
              </Form>

              <div className="loginFooterLink">
                Trebaš nalog?{" "}
                <a href="mailto:info@fixman.ba">Kontaktiraj nas →</a>
              </div>
            </>
          ) : (
            <>
              <div className="loginFormHeader">
                <h1 className="loginTitle">Zaboravljena lozinka</h1>
                <p className="loginSubtitle">
                  Unesite email i poslat ćemo vam link za reset.
                </p>
              </div>

              <span
                className="backToLogin"
                onClick={() => setForgotPassword(false)}
              >
                ← Nazad na prijavu
              </span>

              <Form
                name="forgot-password"
                ref={form}
                layout="vertical"
                style={{ width: "100%" }}
                onFinish={onFinishForgotPassword}
                autoComplete="off"
              >
                <Form.Item<FieldType>
                  name="email"
                  label={t("Email")}
                  rules={[{ required: true, message: t("Please input your email!") }]}
                >
                  <Input placeholder="vas@email.ba" size="large" />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    className="submitBtn"
                  >
                    {t("Submit")}
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}

          <footer className="loginPageFooter">
            © 2026 Fixman HR · <span>Privatnost</span> · <span>Uslovi</span>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Login;
